import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row, Card, CardHeader, CardBody, Input, Label, Button } from "@/Components/ui";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MyEditor from "../../../Components/editor/MyEditor";
import "quill/dist/quill.snow.css";
import { SITE_ID } from "../../../assets/meta/meta";
import { API } from "../../../api/client";
import { dialogService } from "@/utils/dialogService";
import { findMissingRequiredFields, focusFirstMissingField, formatRequiredMessage } from "@/utils/formRequired";

type OptionType = "goods" | "extra" | "extratable" | "hidden" | "soldout" | "deposit";

type OptionDetailResponse = {
    id: number;
    name: string;
    price: number;
    type: OptionType;
    tax_free: boolean;
    description?: string | null;
    image?: string | null;
};

const OPTION_TYPE_OPTIONS: Array<{ value: OptionType; label: string; }> = [
    { value: "goods", label: "상품" },
    { value: "extra", label: "추가 인원" },
    { value: "extratable", label: "추가 테이블" },
    { value: "hidden", label: "숨김" },
    { value: "soldout", label: "품절" },
    { value: "deposit", label: "청소보증금" },
];

const NAME_MAX_LENGTH = 20;
const DESCRIPTION_MAX_LENGTH = 100;

const OptionAddProduct = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = id !== "0";

    const [optionType, setOptionType] = useState<OptionType>("goods");
    const [name, setName] = useState("");
    const [taxFree, setTaxFree] = useState(false);
    const [descript, setDescript] = useState("");
    const [optionImg, setOptionImg] = useState<File | null>(null);
    const [loadedOptionImg, setLoadedOptionImg] = useState("");
    const [price, setPrice] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!optionImg) {
            setPreviewUrl("");
            return;
        }

        const objectUrl = URL.createObjectURL(optionImg);
        setPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [optionImg]);

    const currentImageUrl = useMemo(() => previewUrl || loadedOptionImg, [loadedOptionImg, previewUrl]);

    const fillStates = (data: OptionDetailResponse) => {
        setOptionType(data.type ?? "goods");
        setName(data.name ?? "");
        setPrice(data.price === null || data.price === undefined ? "" : String(data.price));
        setTaxFree(Boolean(data.tax_free));
        setDescript(data.description ?? "");
        setLoadedOptionImg(data.image ?? "");
        setOptionImg(null);
    };

    useEffect(() => {
        if (!isEditMode || !id) {
            return;
        }

        API.get(`/package/options/${id}/`)
            .then((response) => {
                fillStates(response.data);
            })
            .catch(async (error) => {
                await dialogService.handleAlert(error.message, { force: "error" });
            });
    }, [id, isEditMode]);

    const handleImageFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files?.length) {
            setOptionImg(files[0]);
        }
    };

    const buildPayload = async () => {
        const missingFields = findMissingRequiredFields([
            { label: "상품명", value: name, elementId: "productName" },
            { label: "가격", value: price, elementId: "productPrice" },
        ]);

        if (missingFields.length > 0) {
            focusFirstMissingField(missingFields);
            await dialogService.handleAlert(formatRequiredMessage(missingFields));
            return null;
        }

        const trimmedName = name.trim();
        const normalizedPrice = Number(price);

        if (trimmedName.length > NAME_MAX_LENGTH) {
            await dialogService.handleAlert(`상품명은 ${NAME_MAX_LENGTH}자 이하로 입력해주세요.`);
            return null;
        }

        if (!Number.isInteger(normalizedPrice) || normalizedPrice < 0) {
            await dialogService.handleAlert("가격은 0 이상의 정수만 입력할 수 있습니다.");
            return null;
        }

        if (descript.length > DESCRIPTION_MAX_LENGTH) {
            await dialogService.handleAlert(`상세정보는 ${DESCRIPTION_MAX_LENGTH}자 이하로 입력해주세요.`);
            return null;
        }

        const data = new FormData();
        data.append("name", trimmedName);
        data.append("price", String(normalizedPrice));
        data.append("type", optionType);
        data.append("description", descript);
        data.append("tax_free", String(taxFree));
        data.append("site", String(SITE_ID));

        if (optionImg) {
            data.append("image", optionImg, optionImg.name);
        }

        return data;
    };

    const submitData = async () => {
        const data = await buildPayload();
        if (!data) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (isEditMode && id) {
                await API.patch(`/package/options/${id}/`, data, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                await dialogService.handleAlert("수정되었습니다.");
            }
            else {
                await API.post("/package/options/", data, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                await dialogService.handleAlert("생성되었습니다.");
            }
            navigate("/option-product-list");
        }
        catch (error: any) {
            await dialogService.handleAlert(error?.message ?? "저장에 실패했습니다.", { force: "error" });
        }
        finally {
            setIsSubmitting(false);
        }
    };

    return (<React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="옵션 상품 등록 및 수정" pageTitle="옵션"/>

          <Row className="g-4 mb-3">
            <div className="col-sm-auto">
              <div>
                <h4 className="card-title mb-0 grow">등록 및 수정</h4>
              </div>
            </div>
            <div className="col-sm-3 ms-auto">
              <div className="d-flex justify-content-sm-end gap-2">
                <Button color="success" className="add-btn me-1" onClick={submitData} id={isEditMode ? "update-btn" : "create-btn"} disabled={isSubmitting}>
                  {isSubmitting ? "저장 중..." : isEditMode ? "Update" : "Create"}
                </Button>
                <Button color="danger" className="add-btn me-1" onClick={() => {
            navigate("/option-product-list");
        }} id="cancel-btn" disabled={isSubmitting}>
                  Cancel
                </Button>
              </div>
            </div>
          </Row>

          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <div className="live-preview">
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="productName" className="form-label">
                            상품명
                          </Label>
                          <Input type="text" className="form-control" placeholder="상품명" id="productName" value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX_LENGTH}/>
                          <div className="form-text text-end">{name.length}/{NAME_MAX_LENGTH}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="productPrice" className="form-label">
                            가격
                          </Label>
                          <Input type="number" className="form-control" placeholder="가격" id="productPrice" value={price} onChange={(e) => setPrice(e.target.value)} min="0" step="1"/>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="optionType" className="form-label">
                            상품 타입 및 상태
                          </Label>
                          <select value={optionType} onChange={(e) => setOptionType(e.target.value as OptionType)} id="optionType" className="form-select" data-choices data-choices-sorting="true">
                            {OPTION_TYPE_OPTIONS.map((item) => (<option key={item.value} value={item.value}>
                                {item.label}
                              </option>))}
                          </select>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="taxFreeState" className="form-label">
                            면세여부
                          </Label>
                          <select value={String(taxFree)} onChange={(e) => setTaxFree(e.target.value === "true")} id="taxFreeState" className="form-select" data-choices data-choices-sorting="true">
                            <option value="true">면세</option>
                            <option value="false">면세 아님</option>
                          </select>
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="mb-3">
                          <Label htmlFor="seatImg" className="form-label">
                            옵션 썸네일
                          </Label>
                          <Input className="form-control" type="file" id="seatImg" accept="image/jpg, image/jpeg, image/png" onChange={handleImageFiles}/>
                          {currentImageUrl ? (<div className="mt-2">
                              <a href={currentImageUrl} target="_blank" rel="noreferrer">
                                <img width="80px" src={currentImageUrl} alt={`${name || "option"} thumbnail`}/>
                              </a>
                            </div>) : (<span className="text-muted">등록된 이미지 없음</span>)}
                        </div>
                      </Col>
                    </Row>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xxl={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">상세정보</h4>
                </CardHeader>
                <CardBody>
                  <div className="mb-2 text-end text-muted">{descript.length}/{DESCRIPTION_MAX_LENGTH}</div>
                  <div className="snow-editor" style={{ height: 300 }}>
                    <MyEditor value={descript} onChange={setDescript} uid={1}/>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>);
};

export default OptionAddProduct;
